import { Button } from '@1trading/react';
import type { ColumnDef } from '@tanstack/react-table';
import { Download } from 'lucide-react';
import { useMemo, useState } from 'react';
import { UuidCell } from '@/components/data-table/cells';
import { DataTable } from '@/components/data-table/data-table';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { PageHeader } from '@/components/layout/page-header';
import { InstitutionDetailDialog } from '@/features/institutions/institution-detail-dialog';
import { applyFilters, type FilterConfig, type FilterState, TableFilters } from '@/features/institutions/table-filters';
import { useInstitutions } from '@/hooks/queries/compliance/use-institutions';
import { getErrorMessage } from '@/lib/api/error';
import { INSTITUTIONS_CSV_COLUMNS, type InstitutionRow, toInstitutionCsvRow } from '@/services/api/institutions';
import { downloadCsv, toCsv } from '@/utils/csv';

// Shared-table default page size
const PAGE_SIZE = 50;

// Blank cell when the flag is absent — unknown ≠ no, never coerce to false
const OptionalFlagCell = ({ value }: { value: boolean | string | undefined }) => (value === undefined || value === null ? null : <span>{String(value)}</span>);
OptionalFlagCell.displayName = 'OptionalFlagCell';

const COLUMNS: ColumnDef<InstitutionRow, unknown>[] = [
  {
    accessorKey: 'account_holder_id',
    header: 'PID',
    // legacy tooltipify(formatUUID): shortened id + full-value tooltip + copy button
    cell: ({ row }) => <UuidCell value={row.original.account_holder_id} />,
  },
  { accessorKey: 'companyName', header: 'Company' },
  { accessorKey: 'companyStreet', header: 'Street' },
  {
    accessorKey: 'companyIsFinancial',
    // Deliberate fix: legacy column title had a typo — 'Finanacial'
    header: 'Financial',
    enableSorting: false,
    cell: ({ row }) => <OptionalFlagCell value={row.original.companyIsFinancial} />,
  },
  {
    accessorKey: 'isMarketMaker',
    header: 'Market Maker',
    enableSorting: false,
    cell: ({ row }) => <OptionalFlagCell value={row.original.isMarketMaker} />,
  },
  { accessorKey: 'segment', header: 'Segment', enableSorting: false },
  { accessorKey: 'email_address', header: 'Email' },
];

// Deliberate improvement: the three legacy 'multi-select' columns were dead
// config (filterable: false, unsupported type) — ported as working multi-choice filters.
const FILTER_CONFIGS: Array<FilterConfig<InstitutionRow>> = [
  { key: 'account_holder_id', title: 'PID', type: 'multi-text', getValue: (row) => row.account_holder_id },
  { key: 'companyName', title: 'Company', type: 'multi-text', getValue: (row) => row.companyName },
  { key: 'companyStreet', title: 'Street', type: 'multi-text', getValue: (row) => row.companyStreet },
  { key: 'companyIsFinancial', title: 'Financial', type: 'multi-choice', getValue: (row) => row.companyIsFinancial },
  { key: 'isMarketMaker', title: 'Market Maker', type: 'multi-choice', getValue: (row) => row.isMarketMaker },
  { key: 'segment', title: 'Segment', type: 'multi-choice', getValue: (row) => row.segment },
  { key: 'email_address', title: 'Email', type: 'multi-text', getValue: (row) => row.email_address },
];

const InstitutionsPage = () => {
  const { data, isPending, isError, error, refetch } = useInstitutions();
  const [filters, setFilters] = useState<FilterState>({});
  const [selectedRow, setSelectedRow] = useState<InstitutionRow | null>(null);

  const filteredRows = useMemo(() => applyFilters(data ?? [], FILTER_CONFIGS, filters), [data, filters]);

  // Client-side export of the full UNFILTERED dataset; the legacy
  // round-trip through a Nuxt Papa.unparse route is replaced by toCsv.
  const handleDownload = () => {
    if (!data) return;
    downloadCsv(toCsv(data.map(toInstitutionCsvRow), [...INSTITUTIONS_CSV_COLUMNS]), `institutions-${new Date().toISOString()}.csv`);
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        actions={
          <Button className="gap-2" disabled={isPending || isError || (data ?? []).length === 0} onClick={handleDownload} type="button">
            <Download className="size-4" />
            Download Data
          </Button>
        }
        title="Institutions"
      />

      {isError ? (
        // Deliberate improvement: legacy set a network-error flag and rendered nothing
        <QueryErrorBanner message={`Failed to load institutions: ${getErrorMessage(error)}`} onRetry={() => refetch()} />
      ) : (
        <>
          <TableFilters configs={FILTER_CONFIGS} onChange={setFilters} rows={data ?? []} state={filters} />
          <DataTable
            columns={COLUMNS}
            data={filteredRows}
            emptyMessage="No results"
            getRowId={(row) => row.account_holder_id}
            isLoading={isPending}
            onRowClick={setSelectedRow}
            pageSize={PAGE_SIZE}
          />
        </>
      )}

      {selectedRow ? <InstitutionDetailDialog institution={selectedRow} onClose={() => setSelectedRow(null)} /> : null}
    </div>
  );
};
InstitutionsPage.displayName = 'InstitutionsPage';

export default InstitutionsPage;
